import React, { ReactNode } from "react";
import { CancelButton, Indicator, PolyIcon, SubmitButton } from "elements";
import { DetailProps } from "./View/DeviceDetails";
import CallStatus from "./View/CallStatus";
import { infiniteNestLink, Link } from "polyrouter";
import { useProvisioningAccountsQuery } from "generated/graphql";
import { Guard } from "TsRoleGuards";
import { Field, FieldProps } from "collections/Form";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { UnsavedChangesReviewMessage } from "./Settings/MySettings";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

type HeaderDevice = Pick<
  DetailProps,
  | "connected"
  | "id"
  | "hardwareModel"
  | "lastConfigRequestDate"
  | "entityConnections"
  | "supportsSettings"
  | "supportsSoftwareUpdate"
  | "provisioningEnabled"
  | "softwareVersion"
>;

type HeaderActionsProps = {
  device: HeaderDevice | undefined;
  showConfirm?: boolean;
  unsavedChanges?: number;
  onDiscard?: () => void;
  discardKey?: LocalKeys;
  discardIfNotDirty?: boolean;
};

type HeaderWithActionsProps = {
  device: HeaderDevice | undefined;
  pageTitle: ReactNode;
  actions: ReactNode;
};

type HeaderProps = HeaderActionsProps &
  Omit<HeaderWithActionsProps, "actions"> & { hasActionButtons?: boolean };

export const DeviceHeader: React.FC<HeaderProps> = ({
  device,
  pageTitle,
  children,
  hasActionButtons = false,
  showConfirm = false,
  unsavedChanges,
  onDiscard,
  discardKey = "DISCARD",
  discardIfNotDirty = false
}) => {
  const actions = !hasActionButtons ? null : (
    <DeviceHeaderActions
      device={device}
      showConfirm={showConfirm}
      unsavedChanges={unsavedChanges}
      onDiscard={onDiscard}
      discardIfNotDirty={discardIfNotDirty}
    />
  );

  return (
    <DeviceHeaderWithActions
      device={device}
      pageTitle={pageTitle}
      actions={actions}
    >
      {children}
    </DeviceHeaderWithActions>
  );
};

export const DeviceHeaderActions: React.FC<HeaderActionsProps> = ({
  device,
  showConfirm = false,
  unsavedChanges,
  onDiscard,
  discardKey = "DISCARD",
  discardIfNotDirty = false
}) => {
  const { t } = useTranslation();

  return (
    <>
      {unsavedChanges ? (
        <UnsavedChangesReviewMessage
          unsavedChanges={unsavedChanges}
          deviceId={device?.id}
        />
      ) : null}
      <Field name="reset">
        {({ form: { dirty, handleReset, isSubmitting } }: FieldProps) => (
          <CancelButton
            disabled={
              isSubmitting || (!(dirty || unsavedChanges) && !discardIfNotDirty)
            }
            onClick={e => {
              handleReset(e);
              onDiscard && onDiscard();
            }}
          >
            {t(discardKey)}
          </CancelButton>
        )}
      </Field>
      <Field name="submit">
        {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => {
          const disableButton =
            isSubmitting || !(dirty || unsavedChanges) || !isValid;

          return showConfirm ? (
            <Link
              disabled={disableButton}
              to={infiniteNestLink("/confirmApply")}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <span>
                  <PolyIcon icon="refresh" className="refresh spin mr-5" />
                  {t("APPLYING")}
                </span>
              ) : (
                t("APPLY")
              )}
            </Link>
          ) : (
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={disableButton}
            />
          );
        }}
      </Field>
    </>
  );
};

/**
 * This is an abstraction that allows passing whichever buttons or other content
 * desired into the "button space" in the Device Header
 */
export const DeviceHeaderWithActions: React.FC<HeaderWithActionsProps> = ({
  device,
  pageTitle,
  actions,
  children
}) => {
  const tenantId = useTenantId();
  const { data: provData } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenantId }
  });
  const account = provData?.getProvisioningAccounts?.[0];
  const accountProvisioningDisabled = !account || !!account.isDisabled;
  return (
    <div className="header-section">
      <div className="header-information grid pb-1">
        <h1 className="page-title col-6 col-xl-8">
          {device && (
            <Indicator
              device={device}
              accountProvisioningDisabled={accountProvisioningDisabled}
            />
          )}
          {pageTitle}
          <CallStatus id={device?.id} />
        </h1>
        <Guard requirement="update:device">
          <div
            className="save-settings text-right col-6 col-xl-4"
            data-testid="save-settings-buttons"
          >
            {actions}
          </div>
        </Guard>
      </div>
      {children}
    </div>
  );
};
